// Ventas del día (POS → Venta / Caja → Ventas del día, /pos/ventas-dia):
// vista operativa de HOY — facturas, notas de venta y NC — con KPIs, filtro por
// caja/tipo, reimpresión del ticket, anulación de notas de venta y (nivel 5) de
// facturas ya anuladas en el portal del SRI (script 907).
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, CalendarDays, Eye, FileText, Loader2, Printer, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { useAuth } from '@/lib/hooks';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { PanelIntegrado, useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AnularFacturaSriDialog, type FacturaPosAnulable } from './AnularFacturaSriDialog';
import { Caja, cajasActivas, fieldCls, money, posRpc, fechaLocal, useImpresionConfig } from './shared';
import { imprimirTicket, imprimirNotaCredito, ticketHtml, notaCreditoHtml, type EmpresaTicket, type TicketImprimible, type NotaCreditoImprimible } from './print';
import { LOCALE } from '@/lib/utils/regional';

interface DocDia {
  doc_id: string; fecha: string; tipo: 'factura' | 'nota_venta' | 'nc'; codigo: string;
  serie: string | null; clave_acceso: string | null; estado_emision: string | null; ride_url: string | null;
  caja: string; turno: string; cajero: string | null;
  cliente: string | null; identificacion: string | null;
  subtotal: number; iva: number; total: number; estado: string;
  referencia: string | null; motivo: string | null;
}
export interface TicketRpc {
  codigo: string; tipo_doc: 'factura' | 'nota_venta'; fecha: string; estado: string;
  caja: string | null; cajero: string | null; cliente: string | null; identificacion: string | null;
  subtotal: number; iva: number; total: number; recibido: number; vuelto: number;
  clave_acceso: string | null; serie: string | null;
  lineas: { descripcion: string; cantidad: number; precio_unitario: number; descuento: number; subtotal: number }[];
  pagos: { forma: string; monto: number }[];
  // 714: lealtad de la venta + empresa para el encabezado (los usa el Salón)
  sellos?: { campana: string; sellos_ganados: number; premios: number }[];
  puntos_ganados?: number | null; cashback_ganado?: number | null;
  empresa?: { nombre?: string; ruc?: string; direccion?: string; telefono?: string; moneda?: string };
}
// Fila de pos_devoluciones_listar (misma RPC/forma que usa Devoluciones.tsx para imprimir la NC).
interface DevRow {
  id: string; codigo: string; fecha: string; caja: string; cajero: string | null;
  venta: string; factura: string | null; nc_numero: string | null; nc_clave_acceso: string | null; nc_estado: string | null;
  cliente: string; identificacion: string | null; motivo: string; reembolso: string;
  subtotal: number; iva: number; total: number;
  lineas: { descripcion: string; cantidad: number; precio_unitario: number; descuento: number; subtotal: number; reingresa_stock: boolean }[];
}

const hoy = () => fechaLocal();
const hora = (s: string) => new Date(s).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
// Rango de fechas: ventana máxima de 3 meses. Aritmética local sobre 'YYYY-MM-DD'
// (sin desfase de zona) para acotar los inputs y clampear valores tecleados.
const VENTANA_MESES = 3;
const minF = (a: string, b: string) => (a < b ? a : b);
const clampF = (v: string, lo: string, hi: string) => (v < lo ? lo : v > hi ? hi : v);
const sumarMeses = (s: string, n: number): string => {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, (m - 1) + n, d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const ETIQ: Record<DocDia['tipo'], string> = { factura: 'Factura', nota_venta: 'Nota de venta', nc: 'Nota de crédito' };
// Ruta del RIDE en disco → alias público de nginx (igual que en Gestión CXC).
const urlRide = (r: string | null) => (r ? r.replace(/\/opt\/(?:sri\/documentos|plataforma\/sri-docs)/, '/sri-docs') : null);

/** Mapea la respuesta de pos_venta_ticket al objeto imprimible (lo comparte el Salón). */
export const ventaATicket = (t: TicketRpc): TicketImprimible => ({
  codigo: t.codigo, tipoDoc: t.tipo_doc, claveAcceso: t.clave_acceso, serie: t.serie,
  fecha: new Date(t.fecha).toLocaleString(LOCALE), caja: t.caja, cajero: t.cajero,
  cliente: t.cliente ?? '—',
  lineas: t.lineas.map((l) => ({ descripcion: l.descripcion, cantidad: Number(l.cantidad), precio_unitario: Number(l.precio_unitario), descuento: Number(l.descuento), subtotal: Number(l.subtotal) })),
  subtotal: Number(t.subtotal), iva: Number(t.iva), total: Number(t.total),
  recibido: Number(t.recibido), vuelto: Number(t.vuelto), pagos: t.pagos,
  sellos: t.sellos, puntosGanados: Number(t.puntos_ganados) || undefined,
  cashbackGanado: Number(t.cashback_ganado) || undefined,
});
/** Mapea una fila de pos_devoluciones_listar al objeto imprimible de la NC (igual que Devoluciones.tsx). */
const devATicketNc = (d: DevRow): NotaCreditoImprimible => ({
  codigo: d.codigo, ncNumero: d.nc_numero, claveAcceso: d.nc_clave_acceso, facturaNumero: d.factura ?? '—',
  fecha: new Date(d.fecha).toLocaleString(LOCALE), caja: d.caja, cajero: d.cajero,
  cliente: d.cliente, identificacion: d.identificacion, motivo: d.motivo, reembolso: d.reembolso,
  lineas: d.lineas.map((l) => ({ descripcion: l.descripcion, cantidad: Number(l.cantidad), precio_unitario: Number(l.precio_unitario), subtotal: Number(l.subtotal) })),
  subtotal: Number(d.subtotal), iva: Number(d.iva), total: Number(d.total),
});

export function VentasDia() {
  useImpresionConfig();  // deja lista la config del ticket (CFG) antes de reimprimir/previsualizar
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const [anularSri, setAnularSri] = useState<FacturaPosAnulable | null>(null);
  const [caja, setCaja] = useState('');
  const [tipo, setTipo] = useState('todos');
  const [q, setQ] = useState('');
  const [desde, setDesde] = useState(hoy());   // rango a consultar (por defecto, hoy)
  const [hasta, setHasta] = useState(hoy());
  const esHoy = desde === hoy() && hasta === hoy();
  // Límites de la ventana (máx 3 meses; "hasta" no pasa de hoy; "desde" no pasa de "hasta").
  const desdeMin = sumarMeses(hasta, -VENTANA_MESES);
  const hastaMax = minF(hoy(), sumarMeses(desde, VENTANA_MESES));
  const onDesde = (v: string) => setDesde(clampF(v || hoy(), desdeMin, hasta));
  const onHasta = (v: string) => setHasta(clampF(v || hoy(), desde, hastaMax));
  const rangoTxt = desde === hasta ? desde : `${desde} al ${hasta}`;

  const { data: cajas = [] } = useQuery<Caja[]>({ queryKey: ['pos-cajas-activas'], queryFn: cajasActivas });
  const { data: empresa } = useQuery<EmpresaTicket>({
    queryKey: ['pos-emp-ticket'], enabled: !!user?.empresa?.id,
    queryFn: async () => (await apiClient.get<EmpresaTicket[]>(
      `/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=nombre,ruc,direccion,telefono,moneda:moneda_funcional`)).data[0] ?? {},
  });

  const { data: docs = [], isFetching, refetch } = useQuery<DocDia[]>({
    queryKey: ['pos-docs-dia', desde, hasta, caja, tipo],
    queryFn: () => posRpc<DocDia[]>('pos_docs_listar', {
      _desde: desde, _hasta: hasta, _caja: caja || null, _tipo: tipo,
    }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const filtradas = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return docs;
    return docs.filter((d) =>
      d.codigo.toLowerCase().includes(t) || (d.cliente ?? '').toLowerCase().includes(t) ||
      (d.identificacion ?? '').includes(t) || (d.serie ?? '').includes(t));
  }, [docs, q]);

  // KPIs del día (sobre TODO lo consultado, sin el filtro de texto; anuladas fuera)
  const kpi = useMemo(() => {
    const act = docs.filter((d) => d.estado !== 'anulada');
    const fac = act.filter((d) => d.tipo === 'factura');
    const nv = act.filter((d) => d.tipo === 'nota_venta');
    const nc = act.filter((d) => d.tipo === 'nc');
    const sum = (xs: DocDia[]) => xs.reduce((s, d) => s + Number(d.total), 0);
    return {
      facN: fac.length, facT: sum(fac), nvN: nv.length, nvT: sum(nv),
      ncN: nc.length, ncT: sum(nc), neto: sum(act),
    };
  }, [docs]);

  const getVal = useCallback((d: DocDia, key: string): string | number | null => {
    switch (key) {
      case 'hora': return d.fecha;
      case 'tipo': return d.tipo;
      case 'codigo': return d.codigo;
      case 'serie': return d.serie;
      case 'cliente': return d.cliente;
      case 'turno': return d.turno;
      case 'cajero': return d.cajero;
      case 'total': return Number(d.total);
      case 'estado': return d.estado;
      case 'sri': return d.estado_emision;
      default: return null;
    }
  }, []);
  const sort = useSort(filtradas, getVal);
  const filas = sort.sorted;
  const tTot = filas.reduce((s, d) => s + Number(d.total), 0);

  const reimprimir = useMutation({
    mutationFn: (id: string) => posRpc<TicketRpc | null>('pos_venta_ticket', { _venta_id: id }),
    onSuccess: (t) => {
      if (!t) { toast.error('Venta no encontrada'); return; }
      void imprimirTicket(ventaATicket(t), empresa ?? {});
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Previsualización en pantalla (SIN imprimir): factura/nota de venta reutiliza pos_venta_ticket;
  // la NC se resuelve con pos_devoluciones_listar (misma fuente que el "Imprimir NC" de Devoluciones).
  const [preview, setPreview] = useState<{ titulo: string; html: string; imprimir: () => void } | null>(null);
  const ver = useMutation({
    mutationFn: async (d: DocDia): Promise<{ titulo: string; html: string; imprimir: () => void }> => {
      if (d.tipo === 'nc') {
        const rows = await posRpc<DevRow[]>('pos_devoluciones_listar', { _desde: desde, _hasta: hasta });
        const row = rows.find((x) => x.codigo === d.codigo);
        if (!row) throw new Error('No se encontró el detalle de la nota de crédito');
        const nc = devATicketNc(row);
        return { titulo: d.codigo, html: notaCreditoHtml(nc, empresa ?? {}, 'rollo80'), imprimir: () => imprimirNotaCredito(nc, empresa ?? {}, 'rollo80') };
      }
      const t = await posRpc<TicketRpc | null>('pos_venta_ticket', { _venta_id: d.doc_id });
      if (!t) throw new Error('Venta no encontrada');
      const ticket = ventaATicket(t);
      return { titulo: d.codigo, html: await ticketHtml(ticket, empresa ?? {}), imprimir: () => imprimirTicket(ticket, empresa ?? {}) };
    },
    onSuccess: (res) => setPreview(res),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const anular = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      posRpc<{ codigo: string }>('pos_nota_venta_anular', { _venta_id: id, _motivo: motivo }),
    onSuccess: (r) => { toast.success(`Nota de venta ${r.codigo} anulada (stock repuesto)`); void qc.invalidateQueries({ queryKey: ['pos-docs-dia'] }); void qc.invalidateQueries({ queryKey: ['pos-docs'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pedirAnulacion = async (d: DocDia) => {
    const v = await dlg.pedir(`Anular la nota de venta ${d.codigo}`, [
      { key: 'motivo', label: 'Motivo', minimo: 3 },
    ], { detalle: `Total ${money(d.total)}. El stock se repone y el turno debe seguir abierto.`, aceptar: 'Anular' });
    if (v?.motivo.trim()) anular.mutate({ id: d.doc_id, motivo: v.motivo.trim() });
  };

  const headers = ['N°', 'Hora', 'Tipo', 'Código', 'Serie', 'Cliente', 'Identificación', 'Caja', 'Turno', 'Cajero', 'Total', 'Estado', 'SRI'];
  const filasExport: Celda[][] = [
    ...filas.map((d, i) => [i + 1, hora(d.fecha), ETIQ[d.tipo], d.codigo, d.serie, d.cliente, d.identificacion, d.caja, d.turno, d.cajero, Number(d.total), d.estado, d.estado_emision] as Celda[]),
    ['', '', '', '', '', '', '', '', '', `Totales (${filas.length})`, tTot, '', ''] as Celda[],
  ];

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <CalendarDays className="w-5 h-5" /> Ventas del día
          <span className="text-sm font-normal text-muted-foreground">{esHoy ? new Date().toLocaleDateString(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : rangoTxt}</span>
        </h1>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs hover:bg-muted" onClick={() => void refetch()}>
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Refrescar
          </button>
          <ExportarBotones nombre="pos_ventas_dia" titulo="Ventas del día" subtitulo={rangoTxt}
            headers={headers} filas={filasExport} colsDerecha={[0, 10]}
            fmtPDF={(v, col) => (col === 10 && v !== '' ? money(Number(v)) : String(v ?? ''))}
            disabled={!filas.length} />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Facturas ({kpi.facN})</p><p className="text-2xl font-bold tabular-nums">{money(kpi.facT)}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Notas de venta ({kpi.nvN})</p><p className="text-2xl font-bold tabular-nums">{money(kpi.nvT)}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Devoluciones NC ({kpi.ncN})</p><p className={`text-2xl font-bold tabular-nums ${kpi.ncT < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{money(kpi.ncT)}</p></CardContent></Card>
        <Card><CardContent className="pt-4"><p className="text-xs text-muted-foreground">Total neto del día</p><p className="text-2xl font-bold tabular-nums">{money(kpi.neto)}</p></CardContent></Card>
      </div>

      <Card><CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4 items-end">
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Desde</span>
          <input type="date" className={fieldCls} value={desde} min={desdeMin} max={hasta}
            onChange={(e) => onDesde(e.target.value)} /></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Hasta <span className="text-[10px]">(máx. 3 meses)</span></span>
          <input type="date" className={fieldCls} value={hasta} min={desde} max={hastaMax}
            onChange={(e) => onHasta(e.target.value)} /></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Caja</span>
          <select className={fieldCls} value={caja} onChange={(e) => setCaja(e.target.value)}>
            <option value="">— Todas —</option>{cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select></label>
        <label className="block space-y-1"><span className="text-xs text-muted-foreground">Tipo</span>
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="todos">Todos</option><option value="factura">Facturas</option>
            <option value="nota_venta">Notas de venta</option><option value="nc">Notas de crédito</option>
          </select></label>
        <label className="block space-y-1 col-span-2"><span className="text-xs text-muted-foreground">Buscar (código, cliente, identificación, serie)</span>
          <input className={fieldCls} value={q} onChange={(e) => setQ(e.target.value)} placeholder="VTA-000001, NV-, nombre…" /></label>
      </CardContent></Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          Documentos {esHoy ? 'de hoy' : `del ${rangoTxt}`} {isFetching && <Loader2 className="w-4 h-4 animate-spin" />}
          <span className="ml-auto text-xs font-normal text-muted-foreground">Las NC restan · las anuladas no cuentan en los KPIs</span>
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="hora" sort={sort}>Hora</SortTh>
              <SortTh col="tipo" sort={sort}>Tipo</SortTh>
              <SortTh col="codigo" sort={sort}>Código</SortTh>
              <SortTh col="serie" sort={sort}>Serie</SortTh>
              <SortTh col="cliente" sort={sort}>Cliente</SortTh>
              <SortTh col="turno" sort={sort}>Turno</SortTh>
              <SortTh col="cajero" sort={sort}>Cajero</SortTh>
              <SortTh col="total" sort={sort} align="right">Total</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <SortTh col="sri" sort={sort}>SRI</SortTh>
              <th className="py-2 pr-3 text-center">PDF</th>
              <th className="py-2 pr-3 text-right">Ver</th>
              <th className="py-2 pr-3"></th>
            </tr></thead>
            <tbody>
              {filas.length === 0 && <tr><td colSpan={14} className="py-10 text-center text-muted-foreground">{isFetching ? 'Consultando…' : 'Sin documentos en el rango'}</td></tr>}
              {filas.map((d, i) => (
                <tr key={`${d.tipo}-${d.codigo}`} className={`border-b last:border-0 hover:bg-muted/40 ${d.estado === 'anulada' ? 'opacity-50' : ''}`}>
                  <td className="py-1.5 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{hora(d.fecha)}</td>
                  <td className="py-1.5 pr-3">
                    {d.tipo === 'nc'
                      ? <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300" title={`Factura: ${d.referencia ?? '—'} · Motivo: ${d.motivo ?? '—'}`}>NC</span>
                      : d.tipo === 'nota_venta'
                        ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">Nota venta</span>
                        : <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Factura</span>}
                  </td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{d.codigo}{d.referencia ? <span className="text-muted-foreground"> → {d.referencia}</span> : ''}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs" title={d.clave_acceso ?? 'Sin clave de acceso'}>{d.serie ?? '—'}</td>
                  <td className="py-1.5 pr-3 max-w-[14rem] truncate" title={`${d.cliente ?? ''} (${d.identificacion ?? 's/i'})`}>{d.cliente ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono text-xs">{d.turno}</td>
                  <td className="py-1.5 pr-3">{d.cajero ?? '—'}</td>
                  <td className={`py-1.5 pr-3 text-right font-mono tabular-nums font-medium ${Number(d.total) < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>{money(d.total)}</td>
                  <td className="py-1.5 pr-3 text-xs">{d.estado}</td>
                  <td className="py-1.5 pr-3 text-xs">{d.estado_emision ?? '—'}</td>
                  <td className="py-1.5 pr-3 text-center">
                    {urlRide(d.ride_url) ? (
                      <a href={urlRide(d.ride_url)!} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-7 h-7 rounded text-primary hover:bg-muted"
                        title="Ver RIDE (PDF)">
                        <FileText className="w-4 h-4" />
                      </a>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="py-1.5 pr-3 text-right">
                    <button className="rounded p-1 text-muted-foreground hover:bg-muted" title="Previsualizar documento"
                      disabled={ver.isPending} onClick={() => ver.mutate(d)}>
                      {ver.isPending && ver.variables?.doc_id === d.doc_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                    {d.tipo !== 'nc' && (
                      <button className="rounded p-1 text-muted-foreground hover:bg-muted" title="Reimprimir ticket"
                        disabled={reimprimir.isPending} onClick={() => reimprimir.mutate(d.doc_id)}>
                        <Printer className="w-4 h-4" />
                      </button>
                    )}
                    {d.tipo === 'nota_venta' && d.estado === 'registrada' && (
                      <button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Anular esta nota de venta (repone stock; exige turno abierto)"
                        disabled={anular.isPending} onClick={() => void pedirAnulacion(d)}>
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                    {d.tipo === 'factura' && d.estado === 'registrada' && d.estado_emision === 'autorizado' && isAdmin() && (
                      <button className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Anular: la factura ya se anuló en el portal del SRI (repone stock y la saca del cierre)"
                        onClick={() => setAnularSri({ doc_id: d.doc_id, codigo: d.codigo, serie: d.serie, cliente: d.cliente, total: Number(d.total), turno: d.turno })}>
                        <Ban className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filas.length > 0 && (
                <tr className="border-t-2 font-semibold bg-muted/30">
                  <td className="py-2 pr-3" colSpan={8}>Totales ({filas.length})</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(tTot)}</td>
                  <td colSpan={5}></td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {anularSri && <AnularFacturaSriDialog doc={anularSri} onClose={() => setAnularSri(null)}
        onDone={() => { void qc.invalidateQueries({ queryKey: ['pos-docs-dia'] }); void qc.invalidateQueries({ queryKey: ['pos-docs'] }); }} />}

      {preview && (
        <PanelIntegrado title={preview.titulo} onClose={() => setPreview(null)} wide>
          <div className="flex items-center justify-end gap-2 pb-2">
            <button className="flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs hover:bg-muted" onClick={preview.imprimir}>
              <Printer className="h-3.5 w-3.5" /> Imprimir
            </button>
          </div>
          <iframe title="documento" srcDoc={preview.html} className="w-full h-[70vh] bg-white rounded border" />
        </PanelIntegrado>
      )}
    </div>
  );
}
